import { PropertyPageQuery } from 'gql/graphql'
import Link from 'components/Link'
import { Swiper, SwiperSlide } from 'swiper/react'
import useInViewId from 'data/hooks/useInViewId'
import classNames from 'classnames'
import { FreeMode, Mousewheel } from 'swiper/modules'
import { formatEnum, formatShortCurrency, pluraliseWithNumber } from '@ama-selections/ui'
import Paragraphs from 'components/home/common/typography/Paragraphs'
import FixedNavbar from 'components/home/navbar/FixedNavbar'
import Button from 'components/home/common/Button'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from 'store'
import { areDatesSame, asDate, asDateNullable, formatDefault, getDisabledDates } from '@ama-selections/ui/src/helpers/dates'
import { setAdults, setArrival, setChildren, setDeparture } from 'store/booking-request'
import { useEffect, useState } from 'react'
import DateRangeSelectionPopover from 'components/home/navbar/popovers/specific/DateRangeSelectionPopover'
import GuestsSelectionPopover from 'components/home/navbar/popovers/specific/GuestsSelectionPopover'
import { FormProvider, useForm } from 'react-hook-form'
import { DateRangeInput } from 'components/dates/DateRangePicker'
import BookingRequestModal from 'components/modals/home/property/BookingRequestModal'
import { useHasScrolled } from 'data/hooks/useHasScrolled'
import { useQuery } from 'urql'
import { graphql } from 'gql'
import NavbarPopover from 'components/home/navbar/popovers/components/NavbarPopover'
import NavbarButton from 'components/home/navbar/common/NavbarButton'
import { config } from 'data/config'
import { MobileScreenWidths, useScreenWidth } from 'data/hooks/useScreenWidth'
import { useMobileApp } from 'data/hooks/MobileApp/useMobileApp'
import { ExclaimCircleIcon, LoadingIcon } from 'components/icons'

export enum PropertyPageSections {
  GENERAL = 'general',
  BEDS_BATHS = 'beds_&_baths',
  AMENITIES = 'amenities',
  LOCATION = 'location',
  MAP = 'map',
  BOOKING_CONDITIONS = 'booking_conditions',
  SERVICES = 'services',
  MAGAZINE = 'magazine',
  SIMILAR_PROPERTIES = 'similar_properties',
  BECOME_A_MEMBER = 'become-a-member', // `-` as these are already being used
}

interface PropertyNavbarProps {
  property: NonNullable<PropertyPageQuery['property']>
}

export type PropertyNavbarFormFields = {
  dates: DateRangeInput | undefined
  adults: number | undefined
  children: number | undefined
}


const PropertyNavbar = ({ property }: PropertyNavbarProps) => {
  const hasScrolled = useHasScrolled()
  const { isMobile } = useScreenWidth(MobileScreenWidths.LG)
  const { isMobileApp } = useMobileApp()

  const displayedPropertyPageSections = [
    PropertyPageSections.GENERAL,
    PropertyPageSections.BEDS_BATHS,
    PropertyPageSections.AMENITIES,
    PropertyPageSections.LOCATION,
    PropertyPageSections.BOOKING_CONDITIONS,
  ]

  const currentSection = useInViewId(Object.values(PropertyPageSections))

  const [bookingRequestModalOpen, setBookingRequestModalOpen] = useState(false)

  const dispatch = useDispatch()
  const { arrival, departure, adults, children } = useSelector((state: RootState) => ({
    arrival: state.bookingRequest.arrival,
    departure: state.bookingRequest.departure,
    adults: state.bookingRequest.adults,
    children: state.bookingRequest.children,
  }))

  const formProps = useForm<PropertyNavbarFormFields>()

  useEffect(() => {
    if (hasScrolled) {
      formProps.reset({
        dates: {
          startDate: asDate(arrival),
          endDate: asDate(departure),
        },
        adults,
        children,
      })
    }
  }, [hasScrolled])

  // Make sure that Adults are set to Redux
  useEffect(() => {
    if (formProps.watch('adults') !== adults) {
      formProps.setValue('adults', adults)
    }
  }, [adults])

  useEffect(() => {
    const formAdults = formProps.watch('adults')
    if (formAdults !== adults) {
      dispatch(setAdults(formAdults))
    }
  }, [formProps.watch('adults')])

  // Make sure that Children are set to Redux
  useEffect(() => {
    if (formProps.watch('children') !== children) {
      formProps.setValue('children', children)
    }
  }, [children])

  useEffect(() => {
    const formChildren = formProps.watch('children')
    if (formChildren !== children) {
      dispatch(setChildren(formChildren))
    }
  }, [formProps.watch('children')])

  // Make sure that the Arrival is set to Redux
  useEffect(() => {
    const formDates = formProps.watch('dates')
    if (!areDatesSame(formDates?.startDate, arrival)) {
      formProps.setValue('dates', {
        startDate: asDate(arrival),
        endDate: formDates?.endDate,
      })
    }
  }, [arrival])

  useEffect(() => {
    const formStartDate = formProps.watch('dates')?.startDate
    if (!areDatesSame(formStartDate, arrival)) {
      dispatch(setArrival(asDateNullable(formStartDate)))
    }
  }, [formProps.watch('dates')?.startDate])


  // Make sure that the Departure is set to Redux
  useEffect(() => {
    const formDates = formProps.watch('dates')
    if (!areDatesSame(formDates?.endDate, departure)) {
      formProps.setValue('dates', {
        startDate: formDates?.startDate,
        endDate: asDate(departure),
      })
    }
  }, [departure])

  useEffect(() => {
    const formEndDate = formProps.watch('dates')?.endDate
    if (!areDatesSame(formEndDate, departure)) {
      dispatch(setDeparture(asDateNullable(formEndDate)))
    }
  }, [formProps.watch('dates')?.endDate])

  // Availability Query
  const [{ data, fetching }] = useQuery({
    query: graphql(`
      query AvailabilityWithRentalBreakdown ($input: AvailabilityInput!) {
        availability (input: $input) {
          available
          arrival
          departure
          price
          currency
          nights
          rental_breakdown {
            count
            price
          }
        }
      }
    `),
    variables: {
      input: {
        arrival: formatDefault(arrival),
        departure: formatDefault(departure),
        property_id: property?.id,
      },
    },
    pause: !arrival || !departure || !property?.id,
  })

  const mobileHasScrolled = useHasScrolled(1, true)

  return (
    <FixedNavbar
      wishlist={{ enabled: true }}
      share={{ enabled: true }}
      model={property}
      scrollDelay={800}
      scrollDelayOverride={isMobile ? !mobileHasScrolled : true}
      className={{
        children: 'max-w-[854px]',
      }}
    >
      <FormProvider {...formProps}>
        <form className="flex-grow hidden divide-x lg:flex divide-grey-250">
          <DateRangeSelectionPopover
            disabledDates={getDisabledDates(property?.public_events)}
          />

          <GuestsSelectionPopover
            className={{
              container: '!min-w-[206px] !max-w-[206px]',
            }}
          />

          <NavbarPopover
            trigger={
              <NavbarButton
                className={{
                  container: 'pl-40 pr-20',
                }}
                active={!!data?.availability?.price}
              >
                {
                  (
                    arrival && departure
                      ? (
                        data?.availability?.price
                          ? (
                            <div className="flex items-center gap-5">
                              <p>
                                  Rent: ${formatShortCurrency(data?.availability?.price, data?.availability?.currency)}
                              </p>

                              {
                                (!fetching && !data?.availability?.available)
                                  ? <ExclaimCircleIcon className="mb-auto text-11 text-red-75" />
                                  : null
                              }
                            </div>
                          )
                          : (
                            fetching
                              ? <LoadingIcon className="h-24 mx-auto" />
                              : 'Price upon request'
                          )
                      )
                      : (
                        property?.price_range?.min
                          ? `From ${formatShortCurrency(property?.price_range?.min, property?.currency)}/week`
                          : undefined
                      )
                  ) ?? 'Price upon request'
                }
              </NavbarButton>
            }
            variant="absolute"
            className={{
              panel: 'px-30 !pt-30',
            }}
            disabled={!data?.availability?.available}
          >
            <>
              <div className="w-[200px]">
                <div className="flex flex-row justify-between">
                  <Paragraphs.LG>Nightly rate:</Paragraphs.LG>

                  <div className="flex flex-col items-end gap-5">
                    {(data?.availability?.rental_breakdown ?? []).map((night, index) => (
                      <Paragraphs.LG key={index}>
                        {night.count} x {formatShortCurrency(night.price, property?.currency)}
                      </Paragraphs.LG>
                    ))}
                    {
                      data?.availability?.nights && (
                        <Paragraphs.LG>
                          {pluraliseWithNumber(data?.availability?.nights, 'Night')}
                        </Paragraphs.LG>
                      )
                    }
                  </div>
                </div>

                <hr className="my-[12px] w-full" />

                <div className="flex justify-between gap-[56px] mt-[17px]">
                  <Paragraphs.LG>
                    Total rent:
                  </Paragraphs.LG>

                  <Paragraphs.LG>
                    {formatShortCurrency(data?.availability?.price, data?.availability?.currency)}
                  </Paragraphs.LG>
                </div>
              </div>

              <div className="mt-[34px] text-grey-750 whitespace-nowrap">
                <Paragraphs.PropertyPage>Additional refundable damage deposit: {formatShortCurrency((property.security_deposit?.value ?? 0) * 100, data?.availability?.currency)}</Paragraphs.PropertyPage>
                <Paragraphs.PropertyPage>50% of rent due upon reservation.</Paragraphs.PropertyPage>
                <Paragraphs.PropertyPage>50% of rent due {property.property_rental_due?.value} days prior to arrival.</Paragraphs.PropertyPage>
              </div>

              <Paragraphs.PropertyPage className="text-grey-750 mt-[19px]">
                Any doubts? Call us at: <a id="call-ama-selections" href={`tel:${config.amaSelectionsPhoneNumber}`} className="underline text-primary">{config.amaSelectionsDisplayPhoneNumber}</a>
              </Paragraphs.PropertyPage>
            </>
          </NavbarPopover>
        </form>

        <div className="items-center hidden lg:flex">
          <Button
            style="pill"
            onClick={() => setBookingRequestModalOpen(true)}
            className={{
              button: '!px-35',
            }}
          >
            {
              arrival && departure
                ? 'inquire now'
                : 'add dates for price'
            }
          </Button>
        </div>
      </FormProvider>

      <div className={classNames(
        'w-full min-w-0 lg:hidden',
        {
          'hidden': !isMobileApp,
          'flex': isMobileApp,
        })}>
        <Swiper
          modules={[FreeMode, Mousewheel]}
          slidesPerView="auto"
          threshold={10}
          spaceBetween={5}
          slidesOffsetBefore={10}
          slidesOffsetAfter={10}
          freeMode={true}
          mousewheel={{
            forceToAxis: true,
          }}
        >
          {
            displayedPropertyPageSections.map((section) => (
              <SwiperSlide key={section} className="text-16 w-max">
                <Link href={`#${section}`}>
                  <Paragraphs.SM className={classNames(
                    'mx-10 pt-8 pb-[6px] tracking-1/4 !leading-22 font-bold',
                    {
                      'text-grey-800 border-b-2 border-primary-fresh decoration-[2.5px]': section === currentSection,
                      'text-grey-700': section !== currentSection,
                    },
                  )}>
                    {formatEnum(section)}
                  </Paragraphs.SM>
                </Link>
              </SwiperSlide>
            ))
          }
        </Swiper>
      </div>

      <BookingRequestModal
        isOpen={bookingRequestModalOpen}
        onClose={() => setBookingRequestModalOpen(false)}
        property={property}
      />
    </FixedNavbar>
  )
}

export default PropertyNavbar
